import * as React from 'react';

import { DivisionFormatter } from '@longline/aqua-ui/formatters/DivisionFormatter/DivisionFormatter';
import { DateTimeFormatter } from '@longline/aqua-ui/formatters/DateTimeFormatter';
import { CountryFormatter } from '@longline/aqua-ui/formatters/CountryFormatter';
import { NumberFormatter } from '@longline/aqua-ui/formatters/NumberFormatter';
import { Message } from '@longline/aqua-ui/containers/Message';
import { View } from '@longline/aqua-ui/controls/View';
import { SecondaryButton } from '@longline/aqua-ui/controls/SecondaryButton';
import { SVG } from '@longline/aqua-ui/svg';
import { TabBar } from '@longline/aqua-ui/controls/TabBar';
import { ListView } from '@longline/aqua-ui/controls/ListView';
import { PrimaryButton } from '@longline/aqua-ui/controls/PrimaryButton';

import { useSystemsContext } from '../ISystemsContext';
import { useConfirm } from '../../../contexts/confirm/ConfirmContext';
import { Pane } from '../../../ui/Pane';
import { Can } from '../../../ui/Can';
import { LayerUtils } from '../../../util/LayerUtils';
import { IDriverset, ISystemLayer } from '../../../api/models';
import { DriversetViewer } from '../../../ui/DriversetViewer.tsx';
import { Grid } from '../../../ui/Grid';

const ViewSystem = () => {
  const ctx = useSystemsContext();
  const confirmCtx = useConfirm();

  const [driverset, setDriverset] = React.useState(null);

  React.useEffect(() => {
    if(ctx.system && !ctx.system.hydrated) ctx.loadSystem(ctx.system);
  }, [ctx.system]);

  // The mode === 'view' switch in PaneController guarantees ctx.system is
  // populated before this component is rendered; rather than thread `!`
  // through every access, alias once.
  if (!ctx.system) return null;
  const system = ctx.system;

  const handleDelete = () => {
    ctx.deleteSystem();
  }

  const renderContent = () => {
    if(ctx.systemReady == 'failed') {
      return <Message.Error onRetry={() => ctx.loadSystem(system)}>Failed to load system.</Message.Error>;
    }

    return (
      <Grid>
        <Grid.Item weight={6}>
          <View ghost={ctx.systemReady == 'loading'} label="Name" hint="System name">
            {system.name}
          </View>
        </Grid.Item>
        <Grid.Item weight={6}>
          <View ghost={ctx.systemReady == 'loading'} label="Country" hint="System's country">
            {system.country ? <CountryFormatter value={system.country}/> : '\u2014'}
          </View>
        </Grid.Item>
        <Grid.Item weight={6}>
          <View ghost={ctx.systemReady == 'loading'} label="Administrative division" hint="System's administrative division">
            {system.division ? <DivisionFormatter value={system.division}/> : '\u2014'}
          </View>
        </Grid.Item>
        <Grid.Item weight={6}>
          <View ghost={ctx.systemReady == 'loading'} label="Created" hint="System creation date/time">
            <DateTimeFormatter.LongDateTime value={system.created_at ?? ""}/>
          </View>
        </Grid.Item>
        <Grid.Item weight={6}>
          <View ghost={ctx.systemReady == 'loading'} label="Updated" hint="System last update/time">
            <DateTimeFormatter.LongDateTime value={system.updated_at ?? ""}/>
          </View>
        </Grid.Item>
      </Grid>
    )
  }

  return (
    <>
      <Pane.Heading>
        <Pane.Title>{system.name}</Pane.Title>
      </Pane.Heading> 
      
      <Pane.Subheader expandable action={<Can permission="manage_systems"><SecondaryButton title="Delete system" negative icon={SVG.Icons.Delete} onClick={() => confirmCtx.start(handleDelete, "Delete system", "Are you sure you want to delete this system? This will delete all associated layers and driver sets as well.")}/></Can>}>
        <Can permission="manage_systems">Delete system</Can>
      </Pane.Subheader>

      <TabBar active={ctx.tab === 'datasets' ? 1 : (ctx.tab === 'driversets' ? 2 : 0)} line ghost={ctx.systemReady == 'loading'}>
        <TabBar.Tab onClick={() => ctx.setTab('info')}>System info</TabBar.Tab>
        <TabBar.Tab onClick={() => ctx.setTab('datasets')} badge={(system.layers || []).length}>Datasets</TabBar.Tab>
        <TabBar.Tab onClick={() => ctx.setTab('driversets')} badge={(system.driversets || []).length}>Driver sets</TabBar.Tab>
      </TabBar>

      {ctx.tab === 'info' && 
        <Pane.Body>
          {renderContent()}
        </Pane.Body>}

      {ctx.tab === 'datasets' && 
        <ListView
          dark
          ghost={ctx.systemReady == 'loading'}
          data={system.layers || []}
          singular="dataset"
          plural="datasets"
          shadow
          total
          rowHeight={56}
          noData={<Message margin>This system has no datasets.</Message>}
        >
          <ListView.Column active width={2} name="Name" sort="name" defaultSort>{(item: ISystemLayer) => item.name}</ListView.Column>
          <ListView.Column active width={1} name="Type">{(item: ISystemLayer) => LayerUtils.layerTypeToDefinition(item.type).name}</ListView.Column>
          <ListView.Column active width="100px" align="right" name="Points">{(item: ISystemLayer) => <NumberFormatter value={item.data.length} decimals={0}/>}</ListView.Column>
        </ListView>}

      {ctx.tab === 'driversets' && driverset == null &&
        <ListView
          dark
          ghost={ctx.systemReady == 'loading'}
          data={system.driversets}
          singular="driver set"
          plural="driver sets"
          shadow
          total
          rowHeight={56}
          onClick={(item) => {
            setDriverset(item);
          }}
          noData={<Message margin>This system has no driver sets.</Message>}
        >
          <ListView.Column active width={1} name="Name" sort="name" defaultSort>{(item: IDriverset) => item.name}</ListView.Column>
          <ListView.Column active width="100px" align="right" name="Days">{(item: IDriverset) => item.dates.length}</ListView.Column>
        </ListView>}      

      {ctx.tab === 'driversets' && driverset != null && 
        <DriversetViewer driverset={driverset}/>}

      <Pane.Footer>
        <Pane.CancelButton onClick={() => driverset ? setDriverset(null) : ctx.setMode('list')}/>
        <div style={{ display: 'flex', gap: '8px'}}>
          <PrimaryButton onClick={ctx.parseSystem}>LOAD</PrimaryButton>
          <Can permission="manage_systems"><Pane.EditButton disabled={ctx.systemReady == 'loading'} onClick={() => ctx.setMode('edit')}/></Can>
        </div>
      </Pane.Footer>
    </>
  )
}

export { ViewSystem }
